import { type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { Bell, Settings as SettingsIcon, Lock, ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Logo as BrandLogo } from "@/components/brand/Logo";
import { useEntitlements } from "@/hooks/useEntitlements";

function useUnreadCount() {
  return useQuery({
    queryKey: ["learn-unread-notifs"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return 0;
      const { count } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", u.user.id)
        .eq("read", false);
      return count ?? 0;
    },
    refetchInterval: 60_000,
  });
}

/**
 * LearnShell — Bootpreneurs dark themed shell for Sprint 4 learner screens.
 * Replicates the reference design (purple/dark) using scoped HSL tokens
 * defined in index.css under .learn-shell. Other parts of the app keep
 * the global theme.
 */

type NavItem = {
  key: string;
  label: string;
  to: string;
  icon: JSX.Element;
  children?: Array<{ key: string; label: string; to: string; gated?: boolean; gatedTo?: string }>;
  matchPaths?: string[];
};

const Icon = {
  home: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  book: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  cpu: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
    </svg>
  ),
  check: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  users: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
    </svg>
  ),
  award: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </svg>
  ),
  folder: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  briefcase: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  settings: (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

export function Logo({ size = 28 }: { size?: number }) {
  return <BrandLogo size={size} asLink={false} />;
}

function useUserInitials() {
  return useQuery({
    queryKey: ["learn-user-initials"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return { initials: "?", email: "" };
      const { data: p } = await supabase
        .from("profiles")
        .select("first_name, last_name, email")
        .eq("id", u.user.id)
        .maybeSingle();
      const fn = p?.first_name ?? "";
      const ln = p?.last_name ?? "";
      const initials = `${fn[0] ?? ""}${ln[0] ?? ""}`.toUpperCase() || (p?.email ?? u.user.email ?? "?")[0].toUpperCase();
      return { initials, email: p?.email ?? u.user.email ?? "" };
    },
  });
}

export function LearnShell({
  children,
  topBar,
  breadcrumb,
  fullBleed = false,
}: {
  children: ReactNode;
  /** Optional content rendered in the top bar between sidebar trigger and notification icon */
  topBar?: ReactNode;
  /** Optional breadcrumb rendered to the left of the topBar (Phase 3 slot) */
  breadcrumb?: ReactNode;
  /** When true, content renders without the sidebar (used by Lesson Player which has its own header) */
  fullBleed?: boolean;
}) {
  const { pathname } = useLocation();
  const { data: user } = useUserInitials();
  const { data: unreadCount = 0 } = useUnreadCount();
  const { canUseAiMentors } = useEntitlements();

  const items: NavItem[] = [
    { key: "dashboard", label: "Dashboard", to: "/dashboard", icon: Icon.home },
    { key: "myBootcamps", label: "My Bootcamps", to: "/learn", icon: Icon.book },
    {
      key: "mentoring",
      label: "Mentoring",
      to: "/mentoring/hub",
      icon: Icon.cpu,
      matchPaths: ["/mentoring", "/mentor", "/coach", "/strategist"],
      children: [
        { key: "kora", label: "KORA (AI mentor)", to: canUseAiMentors ? "/mentor" : "/mentoring/hub", gated: !canUseAiMentors },
        { key: "velyra", label: "Velyra CEO", to: canUseAiMentors ? "/coach" : "/mentoring/hub", gated: !canUseAiMentors },
        { key: "bruno", label: "Bruno (human)", to: "/mentoring" },
      ],
    },
    { key: "validator", label: "Business Validator", to: "/learn/validator", icon: Icon.check, matchPaths: ["/learn/validator"] },
    { key: "outcomes", label: "Outcomes", to: "/outcomes", icon: Icon.award },
    { key: "services", label: "Services", to: "/services", icon: Icon.briefcase },
    { key: "community", label: "Community", to: "/community", icon: Icon.users },
    { key: "certificates", label: "Certificates", to: "/certificates", icon: Icon.award },
    { key: "resources", label: "Resources", to: "/resources", icon: Icon.folder },
  ];

  const isActive = (to: string) => pathname === to || pathname.startsWith(to + "/");
  const isGroupActive = (item: NavItem) => {
    if (isActive(item.to)) return true;
    return (item.matchPaths ?? []).some((p) => pathname === p || pathname.startsWith(p + "/"));
  };

  if (fullBleed) {
    return <div className="learn-shell flex min-h-screen w-full flex-col">{children}</div>;
  }

  return (
    <div className="learn-shell flex min-h-screen w-full">
      {/* SIDEBAR */}
      <aside
        className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col gap-1 border-r p-3 md:flex"
        style={{
          background: "hsl(var(--learn-surface))",
          borderColor: "hsl(var(--learn-border))",
        }}
      >
        <div className="mb-6 px-2 pt-3">
          <Logo size={28} />
        </div>

        {items.map((item) => {
          const active = isGroupActive(item);
          // Mentoring children should always be visible so users can discover KORA / Velyra / Bruno.
          const expanded = !!item.children;
          return (
            <div key={item.key} className="flex flex-col">
              <Link
                to={item.to}
                className="flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm transition-colors"
                style={{
                  background: active ? "hsl(var(--learn-primary) / 0.12)" : "transparent",
                  color: active ? "hsl(var(--learn-primary-2))" : "hsl(var(--learn-text-muted))",
                  fontWeight: active ? 600 : 500,
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "hsl(var(--learn-surface-5))";
                    e.currentTarget.style.color = "hsl(var(--learn-text-2))";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "hsl(var(--learn-text-muted))";
                  }
                }}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
              {expanded && item.children && (
                <div className="ml-7 mt-0.5 mb-1 flex flex-col gap-0.5 border-l pl-3" style={{ borderColor: "hsl(var(--learn-border))" }}>
                  {item.children.map((c) => {
                    const cActive = isActive(c.to);
                    return (
                      <Link
                        key={c.key}
                        to={c.to}
                        className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[12px] transition-colors"
                        style={{
                          color: cActive ? "hsl(var(--learn-primary-2))" : "hsl(var(--learn-text-muted))",
                          fontWeight: cActive ? 600 : 500,
                        }}
                      >
                        <span className="truncate">{c.label}</span>
                        {c.gated && <Lock size={11} style={{ color: "hsl(var(--learn-warning))" }} />}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Upgrade card */}
        <div
          className="mt-auto rounded-xl p-3.5"
          style={{
            background: "linear-gradient(135deg, hsl(var(--learn-glow)), hsl(var(--learn-surface-2)))",
            border: "1px solid hsl(var(--learn-primary) / 0.2)",
          }}
        >
          <div className="text-xs font-bold" style={{ color: "hsl(var(--learn-text))" }}>
            Explorer plan
          </div>
          <div className="mt-1 text-[11px]" style={{ color: "hsl(var(--learn-text-muted))" }}>
            Upgrade to unlock all bootcamps
          </div>
          <button
            className="mt-2.5 w-full rounded-lg px-3 py-2 text-xs font-bold text-white"
            style={{ background: "linear-gradient(135deg, hsl(var(--learn-primary)), hsl(var(--learn-primary-3)))" }}
          >
            Upgrade to Pro
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="min-w-0 flex-1 px-5 pb-16 pt-6 md:px-8">
        {/* TOP BAR */}
        <div className="mb-8 flex items-center gap-3">
          {breadcrumb && (
            <div className="hidden min-w-0 shrink items-center md:flex">
              {breadcrumb}
            </div>
          )}
          <div className="min-w-0 flex-1">{topBar}</div>
          <Link
            to="/notifications"
            className="relative flex h-10 w-10 items-center justify-center rounded-[10px]"
            style={{
              background: "hsl(var(--learn-surface))",
              border: "1px solid hsl(var(--learn-border))",
              color: "hsl(var(--learn-text-muted))",
            }}
            aria-label="Notifications"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span
                className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-bold text-white"
                style={{ background: "hsl(var(--learn-warning))", border: "2px solid hsl(var(--learn-surface))" }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Link>
          <Link
            to="/settings/profile"
            className="flex h-10 w-10 items-center justify-center rounded-[10px]"
            style={{
              background: "hsl(var(--learn-surface))",
              border: "1px solid hsl(var(--learn-border))",
              color: "hsl(var(--learn-text-muted))",
            }}
            aria-label="Settings"
          >
            <SettingsIcon size={18} />
          </Link>
          <Link
            to="/settings/profile"
            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-extrabold text-white"
            style={{ background: "linear-gradient(135deg, hsl(var(--learn-primary)), hsl(var(--learn-primary-4)))" }}
            title={user?.email ?? ""}
          >
            {user?.initials ?? "·"}
          </Link>
        </div>

        {children}
      </main>
    </div>
  );
}
